import { useCallback, useEffect, useMemo, useRef, type MutableRefObject } from "react";
import { AnimatePresence } from "motion/react";
import { LiveKitRoom, useRoomContext } from "@livekit/components-react";
import {
  ConnectionState,
  RoomEvent,
  type DisconnectReason,
  type Participant,
  type Room,
  type RoomOptions,
} from "livekit-client";
import { useCallSessionStore } from "../store/callSessionStore";
import { videoService } from "../api/video.api";
import {
  useCallReconnect,
  useHeldCredsReconnect,
  type CallReconnectController,
} from "../hooks/useCallReconnect";
import { CallRouteAwareRenderer } from "./CallRouteAwareRenderer";
import { recordCallLeft } from "../hooks/useCallActivityRecorder";
import { CallActivityRecorder, LocalMediaRecovery, LocalMediaStateSync, MediaStallRecovery } from "./CallKeepers";
import type { EvictionSignals } from "../lib/evictionTag";
import { JOINED_OTHER_ROOM_TYPE, parseRoomDataMessage } from "../lib/roomDataMessages";
import { buildCallRoomOptions } from "../lib/callRoomOptions";
import { useNotHeardStore } from "../store/notHeardStore";
import { NotHeardBanner } from "./NotHeardBanner";
import { useCallAudioSession } from "../lib/callAudioSession";

/**
 * Single shared wrapper around the shell-hoisted <LiveKitRoom> (#1178).
 *
 * Both the authenticated Shell and the GuestShell (main.tsx) previously
 * duplicated an identical `<LiveKitRoom onDisconnected={clear}>` block whose
 * naïve "clear on any disconnect" ejected users to the post-call screen the
 * moment a flaky uplink dropped them. This wrapper replaces that block in
 * BOTH shells so the reconnect behaviour lives in exactly one place:
 *
 *  - `onDisconnected` / `onConnected` are driven by `useCallReconnect`, which
 *    keeps re-joining after an *involuntary* drop until it connects (first
 *    with the token already held, via HeldCredsReconnect, then with fresh
 *    ones), and exits cleanly on a deliberate leave or a server-terminal
 *    reason.
 *  - A "reconnecting…" indicator with a Leave action is rendered as a
 *    route-independent overlay so it's visible whether the user is on
 *    /call/<slug> or elsewhere in the suite. There is no "gave up" state
 *    (#2223): a dropped participant is always brought back.
 */
/**
 * Routes call audio to the speaker picked before joining (GH-1859 AC12).
 *
 * The `audioOutput` room option alone is not enough: livekit-client applies it
 * in the Room constructor, before any AudioContext is attached, so with
 * `webAudioMix` only the (muted) <audio> elements get the sink while the
 * context that actually plays the call stays on the OS default. Once the Room
 * has connected its context is attached, so switching then reaches it. Applied
 * once per Room: a reconnect must not undo a speaker picked mid-call.
 */
function CarriedOverSpeaker({ deviceId }: { deviceId?: string }) {
  const room = useRoomContext();
  const appliedTo = useRef<Room | null>(null);

  useEffect(() => {
    if (!deviceId) return;
    const apply = () => {
      if (appliedTo.current === room) return;
      appliedTo.current = room;
      room.switchActiveDevice("audiooutput", deviceId).catch(() => {
        // Output selection unsupported — the Speakers section already says so.
      });
    };
    if (room.state === ConnectionState.Connected) apply();
    room.on(RoomEvent.Connected, apply);
    return () => {
      room.off(RoomEvent.Connected, apply);
    };
  }, [room, deviceId]);

  return null;
}

/**
 * Collects what the backend tells a session just before evicting it to
 * another call (#1918) into `signalsRef`, since the disconnect handler only
 * gets a reason.
 *
 * The marker is a local-participant attribute (snapshotted on every attribute
 * change — it arrives ahead of the leave — and once more on disconnect); the
 * other call's name comes in a data message only this session receives, and
 * is accepted only from the server. Reset on each new connection.
 */
function EvictionSignalsCapture({
  signalsRef,
}: {
  signalsRef: MutableRefObject<EvictionSignals>;
}) {
  const room = useRoomContext();
  useEffect(() => {
    const decoder = new TextDecoder();
    // Always reads the LOCAL participant, so a change on anyone else is a
    // harmless re-read, never a leak of their attributes.
    const snapshot = () => {
      signalsRef.current = {
        ...signalsRef.current,
        attributes: { ...signalsRef.current.attributes, ...room.localParticipant.attributes },
      };
    };
    const onData = (payload: Uint8Array, participant?: Participant) => {
      // Server-sent data has no participant; a peer can't plant a name.
      if (participant) return;
      const msg = parseRoomDataMessage(decoder, payload);
      if (msg?.type !== JOINED_OTHER_ROOM_TYPE || typeof msg.room_name !== "string") return;
      signalsRef.current = { ...signalsRef.current, otherRoomName: msg.room_name };
    };
    const onConnected = () => {
      signalsRef.current = { attributes: { ...room.localParticipant.attributes } };
    };
    room.on(RoomEvent.ParticipantAttributesChanged, snapshot);
    room.on(RoomEvent.DataReceived, onData);
    room.on(RoomEvent.Connected, onConnected);
    room.on(RoomEvent.Disconnected, snapshot);
    return () => {
      room.off(RoomEvent.ParticipantAttributesChanged, snapshot);
      room.off(RoomEvent.DataReceived, onData);
      room.off(RoomEvent.Connected, onConnected);
      room.off(RoomEvent.Disconnected, snapshot);
    };
  }, [room, signalsRef]);

  return null;
}

/**
 * Gives the recovery ladders (#2224 not heard, #2223 can't hear) their full
 * rejoin through the reconnect controller. Registered here because only this
 * wrapper holds both the controller and the room; the guest page registers
 * its own (MeetingRoom's GuestRejoinBridge).
 */
function NotHeardRejoinBridge({
  requestRejoin,
}: {
  requestRejoin: CallReconnectController["requestRejoin"];
}) {
  const room = useRoomContext();
  useEffect(() => {
    const rejoin = () => requestRejoin(() => room.disconnect());
    useNotHeardStore.getState().setRejoin(rejoin);
    return () => {
      if (useNotHeardStore.getState().rejoin === rejoin) useNotHeardStore.getState().setRejoin(null);
    };
  }, [room, requestRejoin]);
  return null;
}

/** How often an admitted-but-not-yet-connected participant re-checks that
 *  the host has not put them back in the waiting list. Same cadence as the
 *  waiting-room poll in RoomRoute. */
const REQUEUE_POLL_MS = 3000;

/**
 * Sends a participant who was admitted from the waiting room back to it if
 * the host puts them back before they manage to connect (#1916).
 *
 * The waiting-room poll in RoomRoute stops at admission, so without this a
 * participant stuck connecting would never learn about it. Polling ends for
 * good the first time the room connects — a later drop is a reconnect, not a
 * failed admission.
 */
function AdmittedRequeueWatcher({
  roomId,
  slug,
  username,
}: {
  roomId: string;
  slug: string;
  username: string;
}) {
  const room = useRoomContext();

  useEffect(() => {
    let done = false;
    const id = setInterval(() => {
      if (done) return;
      if (room.state === ConnectionState.Connected) {
        done = true;
        clearInterval(id);
        return;
      }
      videoService
        .requestEntry(roomId, username)
        .then((res) => {
          if (done || res.status !== "waiting") return;
          done = true;
          clearInterval(id);
          const store = useCallSessionStore.getState();
          store.markRequeued(slug);
          store.markLeaveIntent();
          store.clear();
        })
        .catch(() => {
          // Transient — keep checking until connected or put back.
        });
    }, REQUEUE_POLL_MS);
    return () => {
      done = true;
      clearInterval(id);
    };
  }, [room, roomId, slug, username]);

  return null;
}

/** Lets a rejoin reconnect with the token already held (#2223). */
function HeldCredsReconnect() {
  useHeldCredsReconnect(useRoomContext());
  return null;
}

/** What the session will publish on (re)connect, for the recorder's `joined`. */
const sessionMediaIntent = () => {
  const choices = useCallSessionStore.getState().active?.choices;
  return { mic: !!choices?.audioEnabled, camera: !!choices?.videoEnabled };
};

const syncMediaEnabled = (audioEnabled: boolean, videoEnabled: boolean) =>
  useCallSessionStore.getState().setMediaEnabled(audioEnabled, videoEnabled);

export function CallSessionRoom() {
  const active = useCallSessionStore((s) => s.active);
  const clear = useCallSessionStore((s) => s.clear);
  const markLeaveIntent = useCallSessionStore((s) => s.markLeaveIntent);
  const reconnectStatus = useCallSessionStore((s) => s.reconnectStatus);
  const { handleDisconnected, handleConnected, requestRejoin } = useCallReconnect();
  // Also covers a call resumed after a reload, which has no Join tap.
  useCallAudioSession(Boolean(active));
  const signalsRef = useRef<EvictionSignals>({ attributes: {} });
  const onDisconnected = useCallback(
    (reason?: DisconnectReason) => handleDisconnected(reason, signalsRef.current),
    [handleDisconnected],
  );

  // The devices picked before joining (GH-1859). <LiveKitRoom> builds a new
  // Room whenever `options` changes, so these come from values pinned at Join
  // — a mid-call pick goes through `room.switchActiveDevice`, never through
  // here.
  const audioContext = active?.audioContext;
  const micDeviceId = active?.choices.micDeviceId;
  const camDeviceId = active?.choices.camDeviceId;
  const speakerDeviceId = active?.choices.speakerDeviceId;
  const options = useMemo<RoomOptions>(
    () => buildCallRoomOptions({ audioContext, micDeviceId, camDeviceId, speakerDeviceId }),
    [audioContext, micDeviceId, camDeviceId, speakerDeviceId],
  );

  return (
    <>
      <AnimatePresence>
        {active && (
          <LiveKitRoom
            serverUrl={active.url}
            token={active.token}
            connect
            audio={active.choices.audioEnabled}
            video={active.choices.videoEnabled}
            onDisconnected={onDisconnected}
            onConnected={handleConnected}
            options={options}
            className="contents"
          >
            <CarriedOverSpeaker deviceId={speakerDeviceId} />
            <EvictionSignalsCapture signalsRef={signalsRef} />
            <LocalMediaStateSync onChange={syncMediaEnabled} />
            <LocalMediaRecovery />
            <MediaStallRecovery />
            <CallActivityRecorder roomId={active.roomId} token={active.token} mediaIntent={sessionMediaIntent} />
            <HeldCredsReconnect />
            <NotHeardRejoinBridge requestRejoin={requestRejoin} />
            {active.admittedFromLobby && (
              <AdmittedRequeueWatcher
                roomId={active.roomId}
                slug={active.slug}
                username={active.choices.username}
              />
            )}
            <CallRouteAwareRenderer active={active} />
          </LiveKitRoom>
        )}
      </AnimatePresence>

      {/* Route-independent reconnect + "not heard" banner (#1178, #2224):
          one banner when both hold (AC33). */}
      {active && (
        <NotHeardBanner
          reconnecting={reconnectStatus === "reconnecting"}
          onLeave={() => {
            recordCallLeft();
            markLeaveIntent();
            clear();
          }}
        />
      )}
    </>
  );
}
